import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { checkMicAvailability } from "@/lib/micCheck";
import { showMicHelp, startRecordingWithTimeout } from "@/lib/micHelp";
import { analyzeTake } from "@/lib/audioAnalysis";
import { uploadVocalTake } from "@/lib/creatorVocal";
import { beep, sleep } from "@/lib/clickTone";
import { safeApiError } from "@/lib/safeErrorText";
import { INSTRUMENTAL, adoptChannel, alignTake, applyMix, channelOf, claimChannel, placeBuffer, rememberTake, syncSession, takeBuffer } from "@/components/recstudio/sessionEngine";
import { COUNT_IN, scheduleCountIn } from "@/components/recstudio/beatClick";
import { punchResult } from "@/components/recstudio/punchTake";

const nextTakeName = (track) => `Take ${Math.max(0, ...track.takes.map((t) => Number((t.name.match(/^Take (\d+)/) || [])[1]) || 0), track.takes.length) + 1}`;
const stackOn = (t) => Boolean(t?.stack?.on);
const r3 = (v) => Math.round(v * 1000) / 1000;

/**
 * Record a take onto a session track over everything that's playing: optional 3-2-1 count-in, pause / resume,
 * latency line-up (calibrated or estimated), punch-in over one part, choir-stack voices, an automatic check,
 * then auto-save to Recordings + the session.
 */
export default function useTakeRecorder({ session, trackId, setup }) {
  const { engine, latest, change } = session;
  const [phase, setPhase] = useState("idle");
  const [count, setCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [startPos, setStartPos] = useState(0);
  const [punching, setPunching] = useState(false);
  const run = useRef({});
  const pending = useRef(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => () => {
    run.current.cancel = true;
    clearTimeout(run.current.muteTimer);
    (run.current.countTimers || []).forEach(clearTimeout);
    if (["recording", "paused", "connecting"].includes(phaseRef.current)) {
      const r = run.current;
      engine.stopRecording().catch(() => {}).finally(() => { if (r.prev) Object.assign(engine, { autoTrimSilence: r.prev.autoTrim, recordingMode: r.prev.mode }); });
    }
  }, [engine]);

  useEffect(() => {
    if (phase !== "recording") return undefined;
    const t = setInterval(() => setElapsed(Math.max(0, (Date.now() - run.current.startedAt - run.current.paused) / 1000)), 100);
    return () => clearInterval(t);
  }, [phase]);

  const upload = useCallback(async ({ blob, tid, meta, aligned }) => {
    pending.current = { blob, tid, meta, aligned };
    setSaving(true);
    const doc = latest.current;
    const track = doc.tracks.find((t) => t.id === tid);
    try {
      const rec = await uploadVocalTake(blob, `${doc.title} · ${track?.name || "Vocal"} · ${meta.name}`);
      rememberTake(rec.file_url, aligned);
      const stacked = stackOn(track);
      const idx = channelOf(engine, doc.id, tid);
      if (idx != null && !stacked) engine.channels[idx].recKey = rec.file_url;
      const addTake = (t) => ({
        ...t, takes: [...t.takes, { id: rec.id, file_url: rec.file_url, ...meta }], active_take_id: rec.id,
        ...(stackOn(t) ? { stack: { ...t.stack, take_ids: [...t.stack.take_ids.filter((x) => x !== meta.punch?.from_take_id), rec.id] } } : {}),
      });
      change((d) => ({ ...d, tracks: d.tracks.map((t) => (t.id === tid ? addTake(t) : t)) }), { reload: stacked });
      pending.current = null;
      setResult((r) => (r ? { ...r, saved: true, saveError: null } : r));
      window.dispatchEvent(new Event("elpm:recordings-updated"));
    } catch (e) {
      setResult((r) => (r ? { ...r, saved: false, saveError: safeApiError(e) } : r));
      toast.error("Your take is here but didn't save yet", { description: "Press Save take again." });
    } finally { setSaving(false); }
  }, [change, engine, latest]);

  const start = useCallback(async (opts) => {
    if (phaseRef.current !== "idle") return;
    const doc = latest.current;
    const track = doc?.tracks.find((t) => t.id === trackId);
    if (!track) { toast.error("Pick or add a vocal track first"); return; }
    const punch = opts?.punch ? { ...opts.punch, baseTake: track.takes.find((t) => t.id === track.active_take_id) } : null;
    if (punch && !punch.baseTake) { toast.error("Pick the take you want to fix first"); return; }
    const pre = checkMicAvailability();
    if (pre) { showMicHelp(pre, () => start(opts)); return; }
    run.current = { cancel: false };
    setResult(null);
    // With a known tempo the count-in clicks on the song's beat, right after the mic opens (see below).
    const tempo = doc.instrumental?.tempo;
    const instIdx = channelOf(engine, doc.id, INSTRUMENTAL);
    const startAt = engine.getPlayhead() >= (engine.maxDuration || 0) - 0.05 ? 0 : engine.getPlayhead();
    const onBeat = Boolean(doc.settings.countdown && !punch && tempo && instIdx != null && engine.channels[instIdx].buffer && engine.channels[instIdx].duration > startAt);
    if (doc.settings.countdown && !punch && !onBeat) {
      setPhase("countdown");
      if (engine.ctx.state === "suspended") engine.ctx.resume().catch(() => {});
      for (let k = 3; k > 0; k -= 1) {
        setCount(k);
        beep(engine.ctx, engine.ctx.currentTime + 0.01, { freq: k === 1 ? 1320 : 880, gain: 0.35 });
        await sleep(1000);
        if (run.current.cancel) { setCount(0); setPhase("idle"); return; }
      }
      setCount(0);
    }
    setPhase("connecting");
    let idx;
    try {
      idx = claimChannel(engine, doc.id, track.id);
      if (punch) placeBuffer(engine, doc.id, track.id, await takeBuffer(engine, punch.baseTake), track.name, punch.baseTake.file_url);
    } catch (e) { toast.error(e.message); setPhase("idle"); return; }
    const prev = { autoTrim: engine.autoTrimSilence, mode: engine.recordingMode };
    const from = punch ? Math.max(0, punch.start - punch.preroll) : engine.getPlayhead() >= (engine.maxDuration || 0) - 0.05 ? 0 : engine.getPlayhead();
    engine.pause();
    engine.seekTo(from);
    engine.autoTrimSilence = false;
    engine.recordingMode = doc.settings.monitor && setup.headphones ? "monitor" : "dry";
    const hearOwn = Boolean(punch) || (stackOn(track) && setup.headphones);
    applyMix(engine, doc);
    if (engine.channels[idx].buffer && !hearOwn) engine.setChannelMute(idx, true);
    try {
      await startRecordingWithTimeout(engine, idx, { deviceId: setup.deviceId || null });
    } catch (e) {
      Object.assign(engine, { autoTrimSilence: prev.autoTrim, recordingMode: prev.mode });
      await syncSession(engine, doc).catch(() => {});
      setPhase("idle");
      showMicHelp(e, () => start(opts));
      return;
    }
    const tRec = engine.ctx.currentTime;
    const backing = engine.channels.some((c, i) => (i !== idx || hearOwn) && c.buffer && c.duration > from);
    let tPlay = tRec;
    const count = onBeat && backing ? scheduleCountIn(engine.ctx, tempo, from) : null;
    const countTimers = count ? [...count.times.map((t, i) => setTimeout(() => setCount(COUNT_IN - i), Math.max(0, (t - engine.ctx.currentTime) * 1000))),
      setTimeout(() => setCount(0), Math.max(0, (count.start - engine.ctx.currentTime) * 1000))] : [];
    if (backing) { await engine.play(count?.start); tPlay = engine.startTime + from; }
    const lead = count ? Math.max(0, tPlay - engine.ctx.currentTime) : 0;
    // Punch-in: hear your take during the run-up, then it drops out where you sing the fix.
    const muteTimer = punch ? setTimeout(() => engine.setChannelMute(idx, true), Math.max(0, (punch.start - from) * 1000 - 30)) : null;
    run.current = { ...run.current, idx, prev, tRec, tPlay, startPos: from, backing, trackId: track.id, startedAt: Date.now() + lead * 1000, lead, countEnd: count ? tPlay : 0, countTimers, paused: 0, punch, muteTimer };
    setStartPos(from);
    setPunching(Boolean(punch));
    setElapsed(0);
    setPhase("recording");
  }, [engine, latest, setup, trackId]);

  const pause = useCallback(() => {
    if (phaseRef.current !== "recording" || run.current.punch || engine.ctx.currentTime < (run.current.countEnd || 0)) return;
    engine.recorder?.pause?.();
    engine.pause();
    run.current.pauseAt = Date.now();
    setPhase("paused");
  }, [engine]);

  const resume = useCallback(async () => {
    if (phaseRef.current !== "paused") return;
    if (run.current.backing) { await engine.play(); await sleep(50); }
    engine.recorder?.resume?.();
    run.current.paused += Date.now() - run.current.pauseAt;
    setPhase("recording");
  }, [engine]);

  const stop = useCallback(async () => {
    if (phaseRef.current === "countdown") { run.current.cancel = true; return; }
    if (!["recording", "paused"].includes(phaseRef.current)) return;
    const r = run.current;
    if (r.stopping) return;
    r.stopping = true;
    clearTimeout(r.muteTimer);
    (r.countTimers || []).forEach(clearTimeout);
    setCount(0);
    const cancelled = engine.ctx.currentTime < (r.countEnd || 0);
    if (phaseRef.current === "paused") r.paused += Date.now() - r.pauseAt;
    const elapsedSec = (Date.now() - r.startedAt - r.paused) / 1000 + (r.lead || 0);
    setPhase("processing");
    setPunching(false);
    engine.pause();
    const res = await engine.stopRecording();
    Object.assign(engine, { autoTrimSilence: r.prev.autoTrim, recordingMode: r.prev.mode });
    const doc = latest.current;
    // stopRecording loads the raw take onto the channel (which drops our claim) — take the channel back.
    if (engine.channels[r.idx]?.recOwner == null) adoptChannel(engine, doc.id, r.trackId, r.idx, null);
    const track = doc.tracks.find((t) => t.id === r.trackId);
    if (cancelled) {
      await syncSession(engine, doc).catch(() => {});
      engine.seekTo(r.startPos);
      setPhase("idle");
      toast.message("Recording cancelled during the count-in");
      return;
    }
    if (!res?.buffer || !track) {
      const msg = res?.error?.message || "";
      await syncSession(engine, doc).catch(() => {});
      setResult({ trackId: r.trackId, trackName: track?.name, takeName: null, error: msg, check: { issues: [/silent|no audio/i.test(msg) ? "silent" : "sync"], clipped: 0 } });
      setPhase("idle");
      return;
    }
    const setupLatency = setup.latencyMs != null ? setup.latencyMs / 1000 : null;
    const latency = setupLatency ?? ((engine.ctx.outputLatency || 0) + (engine.ctx.baseLatency || 0));
    const trim = r.backing ? Math.max(0, r.tPlay - r.tRec + latency) : 0;
    const check = analyzeTake(res.buffer, { elapsedSec, calibrated: !r.backing || setupLatency != null });
    const aligned = alignTake(engine.ctx, res.buffer, trim, r.startPos);
    if (r.punch && aligned.duration < r.punch.start + 0.05) {
      await syncSession(engine, doc).catch(() => {});
      engine.seekTo(r.startPos);
      setPhase("idle");
      toast.message("Nothing was recorded inside your selection", { description: "Your take is unchanged. Press Punch in again and keep singing until it stops on its own." });
      return;
    }
    let out;
    try {
      out = r.punch ? await punchResult(engine, r.punch, aligned)
        : { buffer: aligned, blob: res.blob, name: nextTakeName(track), meta: { trim_head_sec: r3(trim), start_sec: r3(r.startPos) } };
    } catch (e) { toast.error("Couldn't build the fixed take", { description: e.message }); await syncSession(engine, doc).catch(() => {}); setPhase("idle"); return; }
    if (out.cut > 0.1) toast.warning(`The fix stopped ${out.cut.toFixed(1)} s early`, { description: "The end of your selection kept the old take. Punch in again to redo it." });
    placeBuffer(engine, doc.id, track.id, out.buffer, track.name, `local:${Date.now()}`);
    applyMix(engine, doc);
    engine.seekTo(r.startPos);
    setResult({ trackId: track.id, trackName: track.name, takeName: out.name, check, saved: false, punch: Boolean(r.punch) });
    setPhase("idle");
    await upload({ blob: out.blob, tid: track.id, aligned: out.buffer,
      meta: { name: out.name, duration: Math.round(out.buffer.duration * 100) / 100, created_at: new Date().toISOString(), check, ...out.meta } });
  }, [engine, latest, setup, upload]);

  // A punch-in stops on its own just after the part you're fixing.
  useEffect(() => {
    const r = run.current;
    if (phase === "recording" && r.punch && r.startPos + elapsed >= r.punch.end + 0.4) stop();
  }, [elapsed, phase, stop]);

  const retrySave = useCallback(() => { if (pending.current) upload(pending.current); }, [upload]);
  return { phase, count, elapsed, startPos, punching, result, saving, start, pause, resume, stop, retrySave, clearResult: () => setResult(null) };
}
